import { useMediaUrlWithState } from "hhn-commons";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { CSSObject } from "@emotion/react";
import ErrorImage from "../assets/images/image-error.png";
import { DisplayImageContainer } from "./DisplayImage.style";
import { DisplayMediaProps } from "./DisplayMediaProps";
import MediaErrorPlaceholder, { MediaErrorKind } from "./MediaErrorPlaceholder";

const DisplayImage = ({
  url,
  alt,
  showLoader = true,
  fullSizeLoader = true,
  onError,
  containerStyle,
  enableZoom,
  onSetMediaUrl,
  errorKind = "image",
  ...rest
}: {
  url: string;
  alt: string;
  onError?: (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
    setImageSource: (source: string | undefined) => void,
  ) => void;
  showLoader?: boolean;
  fullSizeLoader?: boolean;
  containerStyle?: CSSProperties;
  enableZoom?: boolean;
  onSetMediaUrl?: (url: string) => void;
  errorKind?: MediaErrorKind;
  style?: CSSProperties;
  "data-testid"?: string;
} & DisplayMediaProps) => {
  const [internalShowLoader, setInternalShowLoader] = useState(showLoader);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const resetZoom = () => {
    setIsZoomed(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const toggleZoom = () => {
    if (isZoomed) {
      resetZoom();
    } else {
      setIsZoomed(true);
      setZoomLevel(2.5);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isZoomed && containerRef.current && imageRef.current) {
      const container = containerRef.current as HTMLDivElement;
      const image = imageRef.current as HTMLImageElement;
      const rect = container.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const enableXPanning = containerWidth < image.naturalWidth;
      const mouseXPercent = (e.clientX - rect.left) / containerWidth - 0.5;
      const maxX = (containerWidth * (zoomLevel - 1)) / 2;
      const newX = -maxX * (mouseXPercent * 2);
      const enableYPanning = containerHeight < image.naturalHeight;
      // Calculate mouse position relative to the center of the container (in percentage)
      const mouseYPercent = (e.clientY - rect.top) / containerHeight - 0.5;
      // Calculate max movement distance based on zoom level
      const maxY = (containerHeight * (zoomLevel - 1)) / 2;
      // Calculate position (inverse movement to give natural feel)
      const newY = -maxY * (mouseYPercent * 2);

      // Apply boundaries
      setPosition({
        x: enableXPanning ? Math.max(-maxX, Math.min(maxX, newX)) : 0,
        y: enableYPanning ? Math.max(-maxY, Math.min(maxY, newY)) : 0,
      });
    } else {
      setPosition({ x: 0, y: 0 });
    }
  };

  const mediaState = useMediaUrlWithState(url);
  const signedUrl = mediaState.data?.signedUrl;

  useEffect(() => {
    if (signedUrl && onSetMediaUrl) {
      onSetMediaUrl(signedUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedUrl]);

  const [imageSource, setImageSource] = useState<string | undefined>();

  useEffect(() => {
    resetZoom();
  }, [url]);

  useEffect(() => {
    setInternalShowLoader(!isImageLoaded);
  }, [isImageLoaded]);

  useEffect(() => {
    if (signedUrl) {
      setImageSource(signedUrl);
    }
  }, [signedUrl]);

  return (
    <DisplayImageContainer
      ref={containerRef}
      fullSizeLoader={fullSizeLoader}
      isZoomed={isZoomed}
      enableZoom={enableZoom ?? false}
      showLoader={internalShowLoader && !mediaState.error}
      containerStyle={(containerStyle as CSSObject) ?? {}}
      data-testid="message-image-container"
      onClick={enableZoom ? toggleZoom : undefined}
      onMouseMove={enableZoom ? handleMouseMove : undefined}
    >
      {mediaState.error ? (
        <MediaErrorPlaceholder
          variant="inline"
          kind={errorKind}
          onRetry={mediaState.retry}
          data-testid={rest["data-testid"] ?? "display-image-error"}
        />
      ) : imageSource ? (
        <img
          src={imageSource}
          alt={internalShowLoader ? "" : alt}
          ref={imageRef}
          style={{
            ...(rest && rest.style),
            ...(enableZoom && {
              transform: isZoomed
                ? `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`
                : "none",
              transition: "transform 0.1s ease",
            }),
          }}
          onLoad={() => setIsImageLoaded(true)}
          onError={e =>
            onError
              ? onError(e, setImageSource)
              : (e.currentTarget.src = ErrorImage)
          }
          data-testid={rest["data-testid"]}
        />
      ) : null}
    </DisplayImageContainer>
  );
};

export default DisplayImage;
